import { CollapsibleBox, CollapsibleBoxContainer } from '@/components/shared/CollapsibleBox';
import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import { Flex, ResponsiveBreak } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import type React from 'react';
import styled from 'styled-components';
import { PetLandingPageType } from '../shared';
import { getQuestionsAndAnswers } from './questionsData';

const QuestionsWrapper = styled(Section).attrs({ darkBackground: true })`
  width: 100%;
`;

const Heading = styled(SectionTitle)`
  margin-bottom: 24px;
  text-transform: capitalize;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 21px;
    `}
`;

const SubHeading = styled(SectionParagraph)`
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 60px;
    `}
`;

const subTitle = {
  [PetLandingPageType.Dog]:
    'Your dog’s pet insurance can seem complicated, but we’ll explain everything you need to know.',
  [PetLandingPageType.Cat]: 'Pet insurance can seem complicated, so we worked hard to make it simple.',
  [PetLandingPageType.PetInsurance]: 'Pet insurance can be confusing, so we worked hard to make it simple.',
};

interface QuestionsProps {
  petLandingPageType: PetLandingPageType;
}

const Questions: React.FC<QuestionsProps> = ({ petLandingPageType }) => {
  const { trackEvent } = useAnalyticsContext();

  const petFaqTrackEvent = ({ eventName, analytics }: { eventName: string; analytics: string }) =>
    trackEvent(eventName, {
      product: 'pet',
      analytics,
    });

  const trackPetHubLinkClick = (ev: React.SyntheticEvent<HTMLAnchorElement>): void => {
    trackEvent('pet_landing_page_link.clicked', {
      text: 'pet insurance explained',
      destination_url: ev.currentTarget.href,
    });
  };

  return (
    <QuestionsWrapper justifyContent="center" pt={[40, 70]} pb={[50, 80]} px={20}>
      <Flex flexDirection="column" alignItems="center" px={[16, 16, 0]}>
        <Heading>
          {petLandingPageType === PetLandingPageType.Cat ? 'Still Curious?' : 'Got Dog Insurance Questions?'}
        </Heading>
        <SubHeading>
          {subTitle[petLandingPageType]}
          <ResponsiveBreak display={['inline', 'block']} />
          {petLandingPageType === PetLandingPageType.Cat
            ? 'Want to learn even more?'
            : 'Didn’t find what you were looking for?'}
          <ResponsiveBreak display={['block', 'inline']} />
          <a
            target="_blank"
            rel="noreferrer"
            onClick={trackPetHubLinkClick}
            href="https://www.lemonade.com/pet/explained/"
          >
            Check out pet insurance explained.
          </a>
        </SubHeading>
      </Flex>
      <CollapsibleBoxContainer minBoxesHeight="325px" style={{ maxWidth: '1140px' }}>
        {getQuestionsAndAnswers(petLandingPageType).map(({ question, answer, analytics }) => (
          <CollapsibleBox
            key={question}
            title={question}
            content={answer}
            onMouseEnter={() =>
              petFaqTrackEvent({
                eventName: 'faq.hovered',
                analytics,
              })
            }
            onClick={() =>
              petFaqTrackEvent({
                eventName: 'faq.clicked',
                analytics,
              })
            }
          />
        ))}
      </CollapsibleBoxContainer>
    </QuestionsWrapper>
  );
};

export default Questions;
